import { BoxType as ReduxBoxType, RateProductType, RatesType } from '../store/reducers/GetRates/Type';
import { LengthType } from '../types/shipment';

export const resolveCountryCode = (
  countryInput: any,
  countriesNameObj: Record<string, any> = {},
  countries: any[] = [],
): string => {
  if (!countryInput) return '';
  const countryName =
    typeof countryInput === 'string'
      ? countryInput.trim()
      : typeof countryInput === 'object'
      ? (countryInput.countryCode || countryInput.country || countryInput.name || '').trim()
      : '';
  if (!countryName) return '';

  if (countryName.length === 2 && /^[A-Z]{2}$/.test(countryName)) {
    return countryName;
  }

  if (countriesNameObj[countryName]?.countryCode) {
    return countriesNameObj[countryName].countryCode;
  }

  const keys = Object.keys(countriesNameObj);
  const matchedKey = keys.find(k => k.toLowerCase() === countryName.toLowerCase());
  if (matchedKey && countriesNameObj[matchedKey]?.countryCode) {
    return countriesNameObj[matchedKey].countryCode;
  }

  const foundCountry = countries.find(c => {
    if (c?.name?.toLowerCase() === countryName.toLowerCase()) return true;
    if (c?.alternateNames?.some((alt: string) => alt.toLowerCase() === countryName.toLowerCase())) return true;
    return false;
  });

  return foundCountry?.countryCode || '';
};

export const getDutyChargeType = (dutyCharge: any): string => {
  if (!dutyCharge) return 'DDU';
  if (typeof dutyCharge === 'string') return dutyCharge || 'DDU';
  if (typeof dutyCharge === 'object' && typeof dutyCharge.type === 'string') return dutyCharge.type || 'DDU';
  return 'DDU';
};

export interface BuildRateRequestPayloadParams {
  destination: any;
  source: any;
  shipmentType: string;
  userId?: string;
  weight: number;
  value: number;
  boxes: any[];
  unitOfMeasurement: LengthType | string;
  document?: boolean;
  insurance?: boolean;
  packaging?: boolean;
  currency?: string;
  dropOff?: boolean;
  organizationName?: boolean;
  dutyCharge?: any;
  rateFor?: 'get-rates' | 'create-shipment' | 'connect-on';
  countriesNameObj?: Record<string, any>;
  countries?: any[];
  warehouseAddress?: any;
  warehouseId?: string;
  freight?: number;
  freightInsurance?: number;
  incoterm?: string;
}

export function buildRateRequestPayload(params: BuildRateRequestPayloadParams): RatesType {
  const {
    destination,
    source,
    shipmentType,
    userId,
    weight,
    value,
    boxes,
    unitOfMeasurement,
    document = false,
    insurance = false,
    packaging = false,
    currency = 'INR',
    dropOff = false,
    organizationName = false,
    dutyCharge,
    rateFor = 'create-shipment',
    countriesNameObj = {},
    countries = [],
    freight = 0,
    freightInsurance = 0,
    incoterm = 'FOB',
  } = params;

  const destCountry =
    typeof destination?.country === 'string'
      ? destination.country.trim()
      : typeof destination?.country === 'object'
      ? (destination.country?.name || destination.country?.country || '').trim()
      : '';
  const sourceCountry =
    typeof source?.country === 'string'
      ? source.country.trim()
      : typeof source?.country === 'object'
      ? (source.country?.name || source.country?.country || '').trim()
      : '';

  const destCountryCode = resolveCountryCode(destCountry, countriesNameObj, countries);
  const sourceCountryCode = resolveCountryCode(sourceCountry, countriesNameObj, countries);

  const formattedBoxes: ReduxBoxType[] = (boxes || []).map(box => {
    const boxLength = box.length !== undefined && box.length !== null ? String(box.length) : '0';
    const boxWidth = box.width !== undefined && box.width !== null ? String(box.width) : '0';
    const boxHeight = box.height !== undefined && box.height !== null ? String(box.height) : '0';
    const boxWeight = box.weight !== undefined && box.weight !== null ? String(box.weight) : '0';

    const products: RateProductType[] = (box.products || []).map((product: any) => {
      const qtyVal =
        product.quantity !== undefined && product.quantity !== null
          ? Number(product.quantity)
          : product.qty !== undefined && product.qty !== null
          ? Number(product.qty)
          : 0;
      const unitVal =
        product.unitValue !== undefined && product.unitValue !== null
          ? Number(product.unitValue)
          : product.value !== undefined && product.value !== null
          ? Number(product.value)
          : 0;

      return {
        hsnCode: product.HSNCode || product.hsnCode || '',
        qty: qtyVal,
        quantity: qtyVal,
        value: unitVal,
        unitValue: unitVal,
        description: product.description || '',
      };
    });

    return {
      length: boxLength,
      width: boxWidth,
      height: boxHeight,
      weight: boxWeight,
      products,
    };
  });

  return {
    destination: {
      city: destination?.city || '',
      state: destination?.state || '',
      country: destCountry,
      countryCode: destCountryCode,
      pincode: destination?.pincode || '',
    },
    source: {
      city: source?.city || '',
      state: source?.state || '',
      country: sourceCountry,
      countryCode: sourceCountryCode,
      pincode: source?.pincode || '',
    },
    shipmentType: shipmentType || 'parcel',
    userId,
    weight: Number(weight) || 0,
    value: Number(value) || 0,
    package: {
      box: formattedBoxes,
      unitOfMeasurement: (unitOfMeasurement as LengthType) || 'kg-cm',
    },
    document: Boolean(document),
    insurance: Boolean(insurance),
    packaging: Boolean(packaging),
    currency: currency || 'INR',
    dropOff: Boolean(dropOff),
    organizationName: Boolean(organizationName),
    dutyCharge: getDutyChargeType(dutyCharge),
    rateFor,
    freight: Number(freight) || 0,
    freightInsurance: Number(freightInsurance) || 0,
    incoterm: incoterm || 'FOB',
  };
}
